import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { ExternalLinkSvg } from 'components/svgs/ExternalLinkSvg/ExternalLinkSvg';

import { OpTextButton } from '../OpTextButton/OpTextButton';

interface OpExternalLinkProps
  extends Omit<ComponentProps<typeof OpTextButton>, 'title'> {
  url: string;
}

/**
 * OpExternalLink is a custom link component for opening external URLs in new tabs.
 * It's built on OpTextButton and includes an external link icon for visual indication.
 * 
 * Use this component for links that navigate to external websites or resources outside
 * the application. Automatically opens in a new tab with accessibility support.
 * 
 * @example
 * <OpExternalLink url="https://example.com">
 *   Visit Example
 * </OpExternalLink>
 * 
 */
export const OpExternalLink = ({
  className,
  children,
  url,
  onClick,
  ...buttonProps
}: OpExternalLinkProps) => {
  const { t } = useTranslation();

  return (
    <OpTextButton
      className={clsx('op-external-link', className)}
      {...buttonProps}
      title={t('(Opens in new tab)')}
      onClick={(e) => {
        if (onClick) {
          onClick(e);
        }

        window.open(url, '_blank');
      }}
    >
      {children}
      <ExternalLinkSvg />
    </OpTextButton>
  );
};
